<template>
  <div class="tinymce-editor-container">
    <div :id="editorId"></div>
  </div>
</template>

<script>
import emitter from 'element-ui/src/mixins/emitter'
import { isEn } from '@/i18n'
import tinymce from 'tinymce'
import 'tinymce/plugins/image'
import 'tinymce/plugins/imagetools'
import 'tinymce/plugins/link'
import 'tinymce/plugins/media'
import 'tinymce/plugins/table'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/advlist'
import _ from 'lodash'
import { getFilePath, uploadFile } from '@/utils/qiniu'

// 带权限的占位符，目前后端返回值并没有关于是否属于带权限的的字段，所以先写这
const permissionFields = ['microsite_url', 'satisfaction_survey_content']

export default {
  mixins: [emitter],
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: String,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    templateFields: {
      type: Array
    },
    templateReplaceValues: {
      type: Object
    },
    showTemplateField: {
      type: Boolean,
      default: false
    },
    isLayout: {
      type: Boolean,
      default: false
    },
    isCnContent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const editorId = `instance_${new Date().valueOf()}_${parseInt(Math.random() * 1000 + 1)}`
    return {
      isEn,
      editorId,
      editor: null
    }
  },
  computed: {
    isStructuredInterviewTemplate () {
      const content = this.value || ''
      return _.includes(content, 'data-interview-field=') || _.includes(content, 'data-interview-dashboard-button=')
    },
    contentStyle () {
      const style = `
        .custom-tag {
          display: inline-block;
          max-width: 640px;
          &:hover {
            cursor: pointer;
            outline: 3px solid #b4d7ff;
          }
        }
      `
      return style
    }
  },
  created () {

  },
  mounted () {
    this.createEditor()
  },
  beforeDestroy () {
    this.editor = null
  },
  methods: {
    setup (editor) {
      editor.ui.registry.addMenuButton('placeholderButton', {
        text: this.$t('common.btn.placeholder'),
        fetch: (callback) => {
          const items = _.map(this.templateFields, templateField => {
            return {
              type: 'menuitem',
              text: templateField.i18nTitle,
              onAction: () => {
                const templateContent = this.isCnContent ? templateField.content_cn : templateField.content
                let content
                if (_.includes(permissionFields, templateField.title)) {
                  content = `<custom-tag id="custom-tag-${templateField.title}" class="custom-tag"><div>${templateContent}</div></custom-tag>`
                } else {
                  content = templateContent
                }
                editor.insertContent(content)
              }
            }
          })
          callback(items)
        }
      })
      editor.on('keydown', (e) => {
        if (e.keyCode === 13) {
          // 回车键被按下
          const selectedNode = editor.selection.getNode()
          // 如果选中节点的标签名是 'CUSTOM-TAG'
          if (selectedNode.tagName === 'CUSTOM-TAG') {
            e.preventDefault() // 阻止默认的回车事件
            editor.execCommand('mceInsertContent', false, '<div></div>')
          }
        }
      })
    },
    async createEditor () {
      if (!this.editor) {
        const setting = {
          base_url: `${window.location.origin}/vue-static/tinymce`,
          suffix: '.min',
          target: this.$el.firstElementChild,
          noneditable_noneditable_class: 'mceNonEditable',
          min_height: 380,
          content_style: this.contentStyle,
          plugins: ['image', 'imagetools', 'link', 'media', 'table', 'lists', 'advlist', 'noneditable'],
          toolbar: [
            'undo redo pastetext forecolor backcolor bold italic underline strikethrough link alignleft aligncenter alignright alignjustify bullist numlist image removeformat'
          ],
          visual: !this.isStructuredInterviewTemplate,
          setup: this.setup,
          extended_valid_elements: 'email-header, email-footer, custom-tag',
          custom_elements: 'email-header,email-footer,custom-tag',
          templates: this.templateFields,
          template_replace_values: this.templateReplaceValues,
          branding: false,
          resize: true,
          toolbar_mode: 'wrap',
          convert_urls: false,
          relative_urls: false,
          content_css: [
          ],
          language: isEn() ? 'en' : 'zh_CN',
          menubar: false,
          images_upload_handler: this.imagesUploadHandler
        }
        if (this.showTemplateField) {
          setting.plugins.push('template')
          setting.toolbar.push('formatselect fontselect fontsizeselect table placeholderButton')
        } else {
          setting.toolbar.push('formatselect fontselect fontsizeselect table')
        }
        const editors = await tinymce.init(setting)
        const editor = editors[0]
        this.editor = editor
        editor.on('change input undo redo', () => {
          const content = editor.getContent()
          this.$emit('change', content)
          this.dispatch('ElFormItem', 'el.form.change', [content])
        })
      }
      this.editor.setContent(this.value || '')
      this.editor.setMode(this.disabled ? 'readonly' : 'design')
    },
    async imagesUploadHandler (blobInfo, success, failure, progress) {
      const typeName = this.isLayout ? 'layout' : 'template'
      const file = blobInfo.blob()
      const fileName = file.name
      const fileKey = `default/notification/email/${typeName}/${fileName}`
      const resp = await uploadFile(file, fileKey)
      if (resp) {
        this.fileKey = resp.key
        const filePath = getFilePath(this.fileKey)
        success(filePath)
      }
    }
  },
  watch: {
    '$i18n.locale' () {
      if (this.editor) {
        this.editor.settings.language = isEn() ? 'en' : 'zh_CN'
        this.editor.render()
      }
    },
    value: {
      immediate: true,
      handler () {
        const value = this.value || ''
        if (this.editor) {
          const content = this.editor.getContent()
          if (content !== value) {
            this.editor.setContent(value)
            this.editor.setMode(this.disabled ? 'readonly' : 'design')
          }
        }
      }
    },
    editor (newVal, oldVal) {
      if (oldVal) {
        tinymce.remove(oldVal)
      }
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
